import { Cancel, CheckCircle, OpenInNew, PersonSearch, Visibility } from "@mui/icons-material";
import { Button, Chip, MenuItem, TextField, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { eventClick } from "components/helper";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApplicationReviewDialogs from "../Clients/ApplicationReviewDialogs";
import ApplicationRequestDetailsDialog from "./ApplicationRequestDetailsDialog";
import { fetchApplicationRequestQueue } from "../Clients/referralApplicationApi";

const MODULE_LABELS = {
  admission: "Admission",
  visa: "Visa",
  insurance: "Insurance",
  "skill-assessment": "Skill Assessment",
  "rpl-course": "RPL",
  other: "Other",
};

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Returned" },
  { value: "", label: "All" },
];

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "dd MMM yyyy, HH:mm");
};

/**
 * Central review queue for partner-requested applications across all referred clients.
 * Approving creates the real module entry with the partner's details filled in; the per-client
 * Applications tab shows the same queue scoped to its client.
 */
const ReferralApplicationQueue = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canReview = usePermission(P.REFERRAL.APPLICATION_REVIEW);
  const [status, setStatus] = useState("PENDING");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [viewing, setViewing] = useState(null);

  const queryParams = useMemo(
    () => ({ page: pagination.pageIndex, size: pagination.pageSize, status }),
    [pagination.pageIndex, pagination.pageSize, status]
  );
  const debouncedParams = useDebouncedValue(queryParams, 200);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["referral-application-queue", debouncedParams],
    queryFn: () => fetchApplicationRequestQueue(debouncedParams),
    keepPreviousData: true,
    refetchOnMount: true,
  });

  const rows = data?.content ?? [];
  const total = data?.totalItems ?? 0;

  const refresh = () => {
    queryClient.invalidateQueries(["referral-application-queue"]);
    queryClient.invalidateQueries(["referralApplicationRequests"]);
    queryClient.invalidateQueries(["clientApplications"]);
    queryClient.invalidateQueries(["referral-overview"]);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "clientName",
        header: "Client",
        Cell: ({ row }) => (
          <Button
            size="small"
            startIcon={<PersonSearch fontSize="small" />}
            onClick={(event) => {
              // The row itself opens the request details; the name goes to the client.
              event.stopPropagation();
              navigate(`/admin/client/view/${row.original.clientId}`);
            }}
          >
            {row.original.clientName || `Client ${row.original.clientId}`}
          </Button>
        ),
      },
      {
        accessorKey: "partnerName",
        header: "Requested by",
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "moduleType",
        header: "Service",
        Cell: ({ cell }) => (
          <Chip
            size="small"
            color="primary"
            label={MODULE_LABELS[cell.getValue()] || cell.getValue()}
          />
        ),
      },
      {
        accessorKey: "requestNote",
        header: "What they know",
        Cell: ({ row }) => {
          const note = row.original.requestNote;
          const detailCount = Object.keys(row.original.requestDetails || {}).length;
          return (
            <span className="text-sm text-app-text-secondary">
              {note
                ? note.length > 80
                  ? `${note.slice(0, 80)}…`
                  : note
                : detailCount > 0
                ? `${detailCount} known detail${detailCount === 1 ? "" : "s"}`
                : "Just interested"}
            </span>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Requested",
        Cell: ({ cell }) => <span className="text-sm">{formatDateTime(cell.getValue())}</span>,
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => {
          const value = row.original.status;
          return (
            <Chip
              size="small"
              color={value === "APPROVED" ? "success" : value === "PENDING" ? "warning" : "default"}
              label={
                value === "PENDING" ? "Pending" : value === "APPROVED" ? "Approved" : "Returned"
              }
            />
          );
        },
      },
    ],
    [navigate]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    rowCount: total,
    state: { pagination, isLoading, showProgressBars: isFetching },
    onPaginationChange: setPagination,
    manualPagination: true,
    enableRowActions: true,
    positionActionsColumn: "last",
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        if (!eventClick(event)) setViewing(row.original);
      },
      sx: { cursor: "pointer", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } },
    }),
    renderRowActionMenuItems: ({ row, closeMenu }) => {
      const item = row.original;
      const entries = [
        <MenuItem
          key="view"
          onClick={() => {
            closeMenu();
            setViewing(item);
          }}
        >
          <Visibility fontSize="small" className="!mr-2" /> View details
        </MenuItem>,
        <MenuItem
          key="client"
          onClick={() => {
            closeMenu();
            navigate(`/admin/client/view/${item.clientId}?tab=applications`);
          }}
        >
          <OpenInNew fontSize="small" className="!mr-2" /> Open client applications
        </MenuItem>,
      ];
      if (canReview && item.status === "PENDING") {
        entries.push(
          <MenuItem
            key="approve"
            onClick={() => {
              closeMenu();
              setApproving(item);
            }}
          >
            <CheckCircle fontSize="small" className="!mr-2 text-green-600" /> Approve & create entry
          </MenuItem>,
          <MenuItem
            key="reject"
            onClick={() => {
              closeMenu();
              setRejecting(item);
            }}
          >
            <Cancel fontSize="small" className="!mr-2 text-red-600" /> Return to partner
          </MenuItem>
        );
      }
      if (item.status === "APPROVED" && item.linkedModuleId) {
        const editPaths = {
          admission: `/admin/admission/edit/${item.linkedModuleId}`,
          visa: `/admin/visa/edit/${item.linkedModuleId}`,
          insurance: `/admin/insurance/edit/${item.linkedModuleId}`,
          "skill-assessment": `/admin/skill-assessment/edit/${item.linkedModuleId}`,
          "rpl-course": `/admin/rpl-client/edit/${item.linkedModuleId}`,
        };
        const path = editPaths[item.linkedModuleType];
        if (path) {
          entries.push(
            <MenuItem
              key="entry"
              onClick={() => {
                closeMenu();
                navigate(path);
              }}
            >
              <OpenInNew fontSize="small" className="!mr-2" /> Open created entry
            </MenuItem>
          );
        }
      }
      return entries;
    },
    muiTablePaperProps: { elevation: 0, sx: { backgroundImage: "none", boxShadow: "none" } },
    renderTopToolbarCustomActions: () => (
      <TextField
        select
        size="small"
        label="Status"
        value={status}
        onChange={(event) => {
          setStatus(event.target.value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        sx={{ minWidth: 180 }}
      >
        {STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.label} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    ),
  });

  return (
    <div className="p-4">
      <div className="mb-4">
        <Typography variant="h6" className="!font-semibold">
          Application requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Services referral partners asked for on behalf of their clients. Approving creates the
          real entry with whatever the partner already knew filled in.
        </Typography>
      </div>
      <div className="bg-app-surface rounded-lg border border-app-border overflow-hidden">
        <MaterialReactTable table={table} />
      </div>
      <ApplicationReviewDialogs
        approving={approving}
        rejecting={rejecting}
        onClose={() => {
          setApproving(null);
          setRejecting(null);
        }}
        onDone={refresh}
      />
      <ApplicationRequestDetailsDialog
        request={viewing}
        canReview={canReview}
        onClose={() => setViewing(null)}
        onDone={refresh}
      />
    </div>
  );
};

export default ReferralApplicationQueue;
